<template>
  <CompactItem 
    class="family-group-item" 
    :select-mode="selectMode"
    :pageUrl="familyGroupRef?.pageUrl"
    :status-style="statusStyleRef"
    shadow
    :raised="raised"
    @click="emit('click')">
    <template #icon>
      <img src="@/assets/icons/everyone_500.png"/>
    </template>
    <template #title>
      {{ familyGroupRef?.name }}
    </template>
    <template v-if="showStatusRef" #statusText>
      {{ statusTextRef }}
    </template>
    <template #subtitle>
      Group managed by {{ ownerRef?.displayName }}
    </template>
  </CompactItem>
</template>

<style lang="scss" scoped>
.family-group-item {
  max-width: 500px;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue';
import { PrincipalType } from '@/gp/GroupAdminModel'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useUserStore } from '@/gp/UserStore'
import { LoadMode } from '@/util/AsyncData'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { StatusInfo } from '@/util/AppUtil'
import CompactItem from '@/util/CompactItem.vue'

const props = defineProps<{
  familyGroupId?: string
  userId?: string
  selectMode?: boolean
  status?: StatusInfo
  raised?: boolean
}>()

const emit = defineEmits([
  'click',
])

const familyGroupStore = useFamilyGroupStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const userStore = useUserStore()

const memberStatusText = {
  noprofile: 'No profile',
  profileonly: 'Profile only',
  owner: 'Owner',
  member: undefined,
}
const memberStatusStyle = {
  noprofile: 'secondary',
  profileonly: 'secondary',
  owner: 'warning',
  member: undefined,
}

const familyGroupRef = computed(() => familyGroupStore.getAsyncGroup(props.familyGroupId)?.data)
const ownerRef = computed(() => userStore.getAsyncUser(familyGroupRef.value?.ownerId)?.data)
const userRef = computed(() => userStore.getLoadedUserList([props.userId], LoadMode.EnsureLoaded).at(0))
const userMemberRef = computed(() => familyGroupMemberStore.getMembersForUsers([props.userId], LoadMode.EnsureLoaded)
  .find(fm => fm.familyGroupId == props.familyGroupId))
const profileMemberRef = computed(() => dataGroupMemberStore.getMembersForDataGroups([userRef.value?.profileDataGroupId], LoadMode.EnsureLoaded)
  .find(dm => dm.principalType == PrincipalType.FamilyGroup && dm.principalId == props.familyGroupId))

const showStatusRef = computed(() => props.userId || props.status)

const memberStatusRef = computed(() => 
  ownerRef.value?.id == props.userId ? 'owner' :
  !profileMemberRef.value ? 'noprofile' :
  !userMemberRef.value ? 'profileonly' :
  'member')

const statusTextRef = computed(() => props.status?.text ?? memberStatusText[memberStatusRef.value])
const statusStyleRef = computed(() => props.status?.style ?? memberStatusStyle[memberStatusRef.value])

</script>
